import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateUserAttribute, sendUserAttributeVerificationCode, confirmUserAttribute } from 'aws-amplify/auth';
import * as userActions from '../../store/actions/user';
import ErrorMessage from '../../components/ErrorMessage';
import PhoneNumberInput, { isValidE164Phone } from '../../components/PhoneNumberInput';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import { getNextStepPath, getOnboardingStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';
import { LockIcon } from '../../assets/icons';

function PhoneVerification() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const { user, onboardingStatus, gettingOnboardingStatus, settingOnboardingPhoneNumber, setOnboardingPhoneNumberError } = useSelector(
    (state) => state.user,
  );
  const allowedOnboardingCountries = useSelector((state) => state.config?.allowedOnboardingCountries ?? []);
  const allowedCountries = React.useMemo(
    () => (Array.isArray(allowedOnboardingCountries) ? allowedOnboardingCountries : []).map((c) => (c?.code || '').toUpperCase()).filter(Boolean),
    [allowedOnboardingCountries],
  );
  const defaultCountryCode = allowedCountries.length > 0 ? allowedCountries[0] : 'DE';
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(() => String(user?.phoneNumber || '').replace(/\s+/g, '') || '');
  const [view, setView] = useState('enter');
  const inputRefs = useRef([]);
  const phoneInputRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const hasJustVerifiedRef = useRef(false);
  const autoSubmittedCodeRef = useRef('');
  const inputIds = ['code-input-0', 'code-input-1', 'code-input-2', 'code-input-3', 'code-input-4', 'code-input-5'];
  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);
  const nextStepPath = getNextStepPath(location.pathname, onboardingStatus?.steps);
  const isPhoneStepCompleted =
    onboardingStatus?.currentStep && onboardingStatus.currentStep !== 'VerifyPhoneNumber' && Boolean(nextStepPath);
  const isLocked = isPhoneStepCompleted || isKycOrLaterStep;

  useEffect(() => {
    if (
      hasJustVerifiedRef.current &&
      !settingOnboardingPhoneNumber &&
      !setOnboardingPhoneNumberError &&
      onboardingStatus?.currentStep &&
      onboardingStatus.currentStep !== 'VerifyPhoneNumber'
    ) {
      const nextPath = getOnboardingStepPath(onboardingStatus.currentStep);
      if (nextPath) {
        hasJustVerifiedRef.current = false;
        navigate(nextPath, { replace: true });
      }
    }
  }, [settingOnboardingPhoneNumber, setOnboardingPhoneNumberError, onboardingStatus?.currentStep, navigate]);

  useEffect(() => {
    if (user?.phoneNumber) {
      setPhoneNumber(String(user.phoneNumber).replace(/\s+/g, ''));
    }
  }, [user?.phoneNumber]);

  useEffect(() => {
    if (!hasFetchedRef.current && !onboardingStatus && !gettingOnboardingStatus) {
      hasFetchedRef.current = true;
      dispatch(userActions.getOnboardingStatus());
    }
  }, [dispatch, onboardingStatus, gettingOnboardingStatus]);

  useEffect(() => {
    if (isPhoneStepCompleted) {
      setView('enter');
    }
  }, [isPhoneStepCompleted]);

  useEffect(() => {
    if (isLocked) return;
    if (view === 'enter' && phoneInputRef.current) {
      const el = phoneInputRef.current.querySelector?.('input');
      el?.focus?.();
    }
  }, [view, isLocked]);

  useEffect(() => {
    if (isLocked) return;
    if (view === 'verify' && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [view, isLocked]);

  const isValidE164 = (value) => isValidE164Phone(value);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '+49 XXX XXX XXXX';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 9) {
      return `+${cleaned.slice(0, 2)} XXX XXX ${cleaned.slice(-3)}`;
    }
    return phone;
  };

  const verifyCodeViaCognito = useCallback(async (verificationCode) => {
    await confirmUserAttribute({
      userAttributeKey: 'phone_number',
      confirmationCode: verificationCode,
    });
  }, []);

  const submitVerificationCode = useCallback(async (codeDigits = code) => {
    if (isLocked || isVerifying || settingOnboardingPhoneNumber) return;

    const codeString = Array.isArray(codeDigits) ? codeDigits.join('') : String(codeDigits || '');
    if (codeString.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError(null);
    setIsVerifying(true);
    try {
      await verifyCodeViaCognito(codeString);
      hasJustVerifiedRef.current = true;
      dispatch(userActions.setOnboardingPhoneNumber({ phoneNumber }));
    } catch (e) {
      hasJustVerifiedRef.current = false;
      setError(e?.message || e?.detail || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, [code, dispatch, isLocked, isVerifying, phoneNumber, settingOnboardingPhoneNumber, verifyCodeViaCognito]);

  const handleCodeChange = (index, value) => {
    if (isLocked) return;
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);
    if (newCode.join('').length !== 6) {
      autoSubmittedCodeRef.current = '';
    }
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    const codeString = newCode.join('');
    if (codeString.length === 6 && autoSubmittedCodeRef.current !== codeString) {
      autoSubmittedCodeRef.current = codeString;
      submitVerificationCode(newCode);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    if (isLocked) return;
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const pastedCode = pastedData.split('');
      setCode(pastedCode);
      setError(null);
      inputRefs.current[5]?.focus();
      if (autoSubmittedCodeRef.current !== pastedData) {
        autoSubmittedCodeRef.current = pastedData;
        submitVerificationCode(pastedCode);
      }
    }
  };

  const sendOrResendCode = useCallback(async (targetPhoneNumber, isResend = false) => {
    if (!targetPhoneNumber) {
      throw new Error('Phone number not set yet');
    }
    if (isResend) {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
    } else {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: targetPhoneNumber,
        },
      });
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
    }
  }, []);

  const handleVerify = useCallback(() => submitVerificationCode(), [submitVerificationCode]);

  const handleCompletedStepContinue = useCallback(() => {
    if (nextStepPath) {
      navigate(nextStepPath);
    }
  }, [navigate, nextStepPath]);

  const handleKeyDown = (index, e) => {
    if (isLocked) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (code.join('').length === 6 && !settingOnboardingPhoneNumber && !isVerifying) {
        handleVerify();
      }
      return;
    }
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSendCode = useCallback(async () => {
    if (isLocked) return;

    if (!isValidE164(phoneNumber)) {
      setError('Please enter a valid phone number (E.164 format, e.g. +491701234567)');
      return;
    }

    setError(null);
    setIsResending(true);
    try {
      await sendOrResendCode(phoneNumber, false);
      setCode(['', '', '', '', '', '']);
      autoSubmittedCodeRef.current = '';
      setView('verify');
    } catch (e) {
      setError(e?.message || e?.detail || 'Failed to send verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  }, [isLocked, phoneNumber, sendOrResendCode]);

  const handleBackToPhoneEntry = useCallback(() => {
    if (isLocked) return;
    setView('enter');
    setCode(['', '', '', '', '', '']);
    autoSubmittedCodeRef.current = '';
    setError(null);
  }, [isLocked]);

  useEffect(() => {
    if (setOnboardingPhoneNumberError) {
      setError(setOnboardingPhoneNumberError?.message || setOnboardingPhoneNumberError?.detail || 'Failed to update phone number.');
    }
  }, [setOnboardingPhoneNumberError]);

  const isCodeComplete = code.every((digit) => digit !== '');
  const displayError = error;
  const hasPhoneNumber = phoneNumber.replace(/\D/g, '').length > 0;
  const canSend = hasPhoneNumber && !isResending && !isLocked;
  const canVerify = isCodeComplete && !isVerifying && !settingOnboardingPhoneNumber && Boolean(phoneNumber) && !isLocked;

  useEffect(() => {
    const isVerifyingPhone = isVerifying || settingOnboardingPhoneNumber;
    let continueLabel = 'Continue';
    if (view === 'enter' && isResending) {
      continueLabel = 'Sending...';
    } else if (view === 'verify' && isVerifyingPhone) {
      continueLabel = 'Verifying...';
    }
    let onContinue = handleVerify;
    if (isPhoneStepCompleted) {
      onContinue = handleCompletedStepContinue;
    } else if (view === 'enter') {
      onContinue = handleSendCode;
    }
    let continueDisabled = view === 'enter' ? !canSend : !canVerify;
    if (isPhoneStepCompleted) {
      continueDisabled = false;
    }

    setFooterConfig({
      continueLabel,
      continueDisabled,
      onContinue,
      backDisabled: isLocked || view !== 'verify' || isResending || isVerifyingPhone,
      onBack: !isLocked && view === 'verify' ? handleBackToPhoneEntry : undefined,
    });

    return () => setFooterConfig({});
  }, [
    canSend,
    canVerify,
    handleBackToPhoneEntry,
    handleCompletedStepContinue,
    handleSendCode,
    handleVerify,
    isLocked,
    isPhoneStepCompleted,
    isResending,
    isVerifying,
    setFooterConfig,
    settingOnboardingPhoneNumber,
    view,
  ]);

  if (view === 'enter') {
    return (
      <div className='flex justify-center px-4 pb-24 pt-[6vh] sm:px-6'>
        <div className='w-full max-w-[540px] rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
          <div>
            <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Secure your account.</h1>
            <p className='mt-2 text-sm leading-6 text-[#4E6F8A]'>We&apos;ll send a verification code to your phone.</p>
          </div>
          <div className={`mt-7 space-y-6 ${isLocked ? 'pointer-events-none opacity-60' : ''}`}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (canSend) {
                  handleSendCode();
                }
              }}
              className='space-y-6'
            >
              <fieldset disabled={isLocked} className='min-w-0 border-0 p-0'>
              <div className='space-y-1.5'>
                <label htmlFor='phone' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
                  Phone number
                </label>
                <div ref={phoneInputRef}>
                  <PhoneNumberInput
                    id='phone'
                    value={phoneNumber}
                    onChange={(v) => setPhoneNumber(String(v || '').replace(/\s+/g, ''))}
                    disabled={isLocked}
                    allowedCountries={allowedCountries}
                    defaultCountryCode={defaultCountryCode}
                    selectClassName='flex h-12 items-center rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white text-sm text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
                    inputClassName='flex h-12 flex-1 min-w-0 rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 disabled:cursor-not-allowed disabled:opacity-50'
                    placeholder='e.g. 1701234567'
                  />
                </div>
                {displayError && <ErrorMessage error={displayError} className='mt-2' />}
              </div>
              <div className='text-center'>
                <p className='text-xs leading-5 text-[#7A9AB0]'>We&apos;ll send you a 6-digit code to verify your phone number.</p>
              </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex justify-center px-4 pb-24 pt-[6vh] sm:px-6'>
      <div className='w-full max-w-[540px]'>
        <div className='rounded-[20px] bg-white px-6 py-9 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
          <div className='text-center'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#0F2540]'>
              <LockIcon className='h-7 w-7' strokeWidth='1.75' />
            </div>
            <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Enter verification code.</h1>
            <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>
              Enter the 6-digit code sent to <b className='font-semibold text-[#0B1929]'>{formatPhoneNumber(phoneNumber)}</b>
            </p>
          </div>
          <div className={`mt-7 space-y-5 ${isLocked ? 'pointer-events-none opacity-60' : ''}`}>
            <fieldset disabled={isLocked} className='min-w-0 border-0 p-0'>
            <div className='space-y-2'>
              <div className='flex justify-center'>
                <div className='inline-grid grid-cols-6 gap-1.5 sm:gap-2'>
                  {code.map((digit, index) => {
                    const inputRef = (el) => {
                      if (el) {
                        inputRefs.current[index] = el;
                      }
                    };
                    return (
                      <input
                        key={inputIds[index]}
                        ref={inputRef}
                        type='text'
                        inputMode='numeric'
                        maxLength='1'
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        disabled={isLocked}
                        className='flex h-12 min-w-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#D8E4ED] bg-white text-center text-[20px] font-bold text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 sm:h-14 sm:w-12 sm:text-[22px]'
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            <div className='text-center text-[13px] text-[#7A9AB0]'>
              <span>Didn&apos;t receive the code? </span>
              <button
                type='button'
                onClick={async () => {
                  setError(null);
                  setIsResending(true);
                  try {
                    await sendOrResendCode(phoneNumber, true);
                    setCode(['', '', '', '', '', '']);
                    autoSubmittedCodeRef.current = '';
                    inputRefs.current[0]?.focus();
                  } catch (e) {
                    setError(e?.message || e?.detail || 'Failed to resend code. Please try again.');
                  } finally {
                    setIsResending(false);
                  }
                }}
                disabled={isResending || isVerifying || !phoneNumber || isLocked}
                className='font-semibold text-[#2E6BAD] transition hover:text-[#0B1929] disabled:cursor-not-allowed disabled:opacity-50'
              >
                {isResending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
            {displayError && <ErrorMessage error={displayError} className='text-center' />}
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneVerification;
